import * as mwx from '@/utils/common.js';
import { rotateImage } from '@/utils/cropImage.js';
import { Cache } from './Cache.js';
const FileType = require('file-type/browser');

// url-filename 映射存储
export const urlFilenameMap = {};

/**
 * Promise 超时控制，如果 promise 在指定时间内未完成，则返回默认值
 *
 * @param {Object} params - 参数对象
 * @param {Promise} params.promise - 需要执行的 Promise
 * @param {number} params.time - 超时时间（毫秒）
 * @param {*} params.result - 超时后返回的默认值
 * @returns {Promise<*>} 返回 promise 的结果或超时后的默认值
 *
 * @description
 * 使用 Promise.race 实现超时控制：
 * - 如果 promise 在 time 时间内完成，返回 promise 的结果
 * - 如果超时，返回预设的 result 默认值
 */
function promiseTimeout({ promise, time, result }) {
  let excute = false;
  let timeoutPromise = new Promise((resolve) => {
    let id = setTimeout(() => {
      clearTimeout(id);
      if (!excute) {
        resolve(result);
        console.log('promiseTimeout 返回默认值', result);
      }
    }, time);
  });

  return Promise.race([promise, timeoutPromise]).then((r) => {
    excute = true;
    return r;
  });
}

/**
 * 通过 URL 获取文件大小（MB），不使用 fetch API
 * 支持 base64 和 blob URL
 *
 * @param {string} url - 图片 URL (base64 或 blob URL)
 * @returns {Promise<number|null>} 返回文件大小（MB），解析失败时返回 null
 *
 * @description
 * - base64 URL：直接从 base64 字符串长度计算文件大小
 *   - 计算公式：(base64Length / 4) * 3 - paddingCount（字节数）
 * - blob URL：通过 XHR 请求获取 blob 对象及其 size
 */
function getUrlFileMb(url) {
  if (!url) return Promise.resolve(null);

  // base64 URL：直接从字符串计算大小
  if (/^data:/.test(url)) {
    try {
      const parts = url.split(',');
      const base64Data = parts[1];
      if (!base64Data) return Promise.resolve(null);
      const paddingCount = (base64Data.match(/=+$/) || [''])[0].length;
      const byteLength = (base64Data.length / 4) * 3 - paddingCount;
      const mb = byteLength / 1024 / 1024;
      return Promise.resolve(mb);
    } catch (e) {
      return Promise.resolve(null);
    }
  }

  // blob URL：使用 XHR 获取文件大小
  if (/^blob:/.test(url)) {
    return new Promise((resolve) => {
      const xhr = new XMLHttpRequest();
      xhr.open('GET', url, true);
      xhr.responseType = 'blob';
      xhr.onload = function () {
        if (xhr.status === 200 && xhr.response) {
          const mb = xhr.response.size / 1024 / 1024;
          resolve(mb);
        } else {
          resolve(null);
        }
      };
      xhr.onerror = function () {
        resolve(null);
      };
      xhr.send();
    });
  }

  return Promise.resolve(null);
}

/**
 * 旋转和压缩图片列表
 *
 * @param {Object} params - 参数对象
 * @param {Array<string>} params.imgList - 图片 URL 列表
 * @param {number} [params.templateImageRatio] - 模板图片宽高比
 * @param {boolean} [params.disableRotate=true] - 是否禁用自动旋转
 * @param {Object} [params.minifyOptions={}] - 压缩配置选项
 * @returns {Promise<Array>} 返回压缩后的图片列表
 * @throws {Error} 当压缩失败时抛出错误
 *
 * @description
 * 处理流程：
 * 1. 如果未禁用旋转，设置自动旋转选项
 * 2. 使用 compressImage 对每张图片进行压缩
 * 3. 返回压缩后的图片列表
 */
async function rotateAndMinifyImage({
  imgList,
  templateImageRatio,
  disableRotate = true,
  minifyOptions = {}
}) {
  minifyOptions.maxPngMb = undefined; //先关闭
  let startTime = new Date();
  if (!disableRotate) {
    minifyOptions.autoRotateImage = true;
    minifyOptions.templateImageRatio = templateImageRatio;
  }
  // minifyOptions.customImageType = 'image/jpeg'; // png 图片太大, 强制压缩为jpeg

  // mwx.slog.log('图片旋转耗时', new Date() - startTime);
  // mwx.slog.log('生成的文件大小', imgList[0].length / 1024 / 1024);

  // 图片压缩：1. blobUrl获取file 进行压缩 2. base64直接压缩
  async function processOneUrl(url) {
    if (!minifyOptions.maxPngMb) {
      return mwx.compressImage(url, minifyOptions);
    }
    let fileMb = await getUrlFileMb(url);
    if (fileMb === null) {
      return mwx.compressImage(url, minifyOptions);
    }
    let isRetouch = false;
    try {
      isRetouch = await checkIfRetouchImage(url);
    } catch (err) {
      mwx.slog.log('rotateAndMinifyImage checkIfRetouchImage fail');
      mwx.slog.error(err);
      isRetouch = false;
    }
    if (isRetouch && fileMb <= minifyOptions.maxPngMb) {
      return url;
    }
    return mwx.compressImage(url, minifyOptions);
  }

  // 图片压缩：1. blobUrl获取file 进行压缩 2. base64直接压缩
  try {
    imgList = await Promise.all(imgList.map((url) => processOneUrl(url)));
  } catch (error) {
    mwx.slog.log('rotateAndMinifyImage fail');
    mwx.slog.error(error);
    throw new Error(mwx.failReasonMap.minifyFail);
  }
  return imgList;
}

/**
 * 将列表按照指定数量进行拆分
 * 将 list 按照 divideCount 进行拆分，拆分成二维数组，每个子数组的数量小于等于 divideCount
 *
 * @param {Object} params - 参数对象
 * @param {Object} params.sku - SKU 信息对象
 * @param {number} params.sku.qty - SKU 数量
 * @param {Array} params.list - 需要拆分的列表
 * @param {number} params.divideCount - 每组的数量限制
 * @returns {Array<Array>} 返回拆分后的二维数组
 *
 * @description
 * 拆分规则：
 * 1. 累加每个元素的 qty，当达到 divideCount 时进行拆分
 * 2. 如果当前数量小于 divideCount，但加上下一个会超过，则先拆分
 * 3. 最后未满的组会合并到之前未满的组中
 * 4. 拆分组数不超过 sku.qty
 */
function divideListByCount({ sku, list, divideCount }) {
  // 生活照相同sku进行拆分
  let listArr = [];
  let count = 0;
  let arr = [];
  let unFullIndex = -1;
  list.forEach((v, ind) => {
    arr.push(v);
    count += v.qty;
    // 当前的数量等于divideCount，或者下一个数量加上当前数量大于divideCount时，进行拆分
    if (count == divideCount) {
      listArr.push(arr);
      arr = [];
      count = 0;
    } else if (
      count < divideCount &&
      list[ind + 1] &&
      count + list[ind + 1].qty > divideCount
    ) {
      listArr.push(arr);
      unFullIndex = listArr.length - 1;
      arr = [];
      count = 0;
    }
  });
  // 最后一组数据, 如果存在没有满的组都塞里面
  if (arr.length) {
    if (unFullIndex != -1) {
      listArr[unFullIndex] = [...listArr[unFullIndex], ...arr];
    } else {
      listArr.push(arr);
    }
  }
  if (listArr.length > sku.qty) {
    // 分组有问题
    listArr = listArr.slice(0, sku.qty);
  }
  return listArr;
}

/**
 * 获取当前正在编辑的子订单列表数据
 *
 * @param {Array} list - 生活照所有 list 的数据（单个 sku 多个数量）
 * @param {Object} params - 参数对象
 * @param {Object} params.sku - SKU 信息对象
 * @param {number} params.sku.qty - SKU 数量
 * @param {Object} params.sku.template - 模板信息
 * @param {number} params.sku.template.bgCount - 背景数量
 * @param {number} [params.lifeSubIndex=0] - 正在编辑的子订单索引
 * @returns {Array} 返回当前子订单的列表数据
 *
 * @description
 * 如果 sku.qty == 1，直接返回完整列表
 * 如果 sku.qty > 1，将列表拆分后返回对应索引的子列表
 */
function getCurrentList(list, { sku, lifeSubIndex = 0 }) {
  if (sku.qty == 1 || sku.qty == undefined) {
    return list;
  } else {
    let listArr = divideListByCount({
      sku,
      list,
      divideCount: sku.template.bgCount
    });
    return listArr[lifeSubIndex] || [];
  }
}

/**
 * 修复没有 cropInfo 的数据
 * 为缺少 cropInfo 的图片数据自动生成默认的裁剪信息
 *
 * @param {Array} currentLifeList - 当前生活照列表
 * @returns {Promise<Array>} 返回修复后的列表
 * @throws {Error} 当图片信息获取失败时抛出错误
 *
 * @description
 * 处理流程：
 * 1. 检查列表中是否存在没有 cropInfo 的项
 * 2. 如果存在，显示加载提示
 * 3. 获取所有缺失 cropInfo 的图片信息
 * 4. 为每个图片生成默认的 cropInfo（包含边框、宽高、模式等）
 * 5. 隐藏加载提示并返回修复后的列表
 */
async function fixNoCropInfoData(currentLifeList) {
  let noCropInfo =
    currentLifeList.length && currentLifeList.filter((v) => !v.cropInfo).length;
  if (noCropInfo) {
    mwx.showLoading('正在加载中...');
    try {
      let downloadImageList = currentLifeList.map((v) => {
        return mwx.getImageInfo(v.imageUrlList[0]);
      });
      let imageInfoList = await Promise.all(downloadImageList);

      currentLifeList.forEach((v, imgIndex) => {
        v.cropInfo = {
          border: 0,
          w: imageInfoList[imgIndex].width,
          h: imageInfoList[imgIndex].height,
          width: imageInfoList[imgIndex].width,
          height: imageInfoList[imgIndex].height,
          mode: 0,
          layout: 'fill'
        };
      });
    } catch (error) {
      throw error;
    }
    mwx.hideLoading();
  }
  return currentLifeList;
}

/**
 * 获取拍立得尺寸信息
 * 解析拍立得模板的尺寸数据，返回外框和内框的尺寸信息
 *
 * @param {Object} [pldSizeInfo] - 拍立得尺寸信息对象
 * @param {number} pldSizeInfo.imageWidth - 外框宽度
 * @param {number} pldSizeInfo.imageHeight - 外框高度
 * @param {Array<number>} pldSizeInfo.rect - 内框信息 [left, top, width, height]
 * @returns {Object} 返回尺寸信息对象
 * @returns {number} Object.outerWidth - 外框宽度
 * @returns {number} Object.outerHeight - 外框高度
 * @returns {number} Object.left - 内框距离外框左边距
 * @returns {number} Object.top - 内框距离外框上边距
 * @returns {number} Object.width - 内框宽度
 * @returns {number} Object.height - 内框高度
 *
 * @description
 * 如果未传入参数，从 Store 中获取 'pldSizeInfo'
 */
function getPldSizeInfo(pldSizeInfo) {
  if (!pldSizeInfo) {
    pldSizeInfo = mwx.Store.get('pldSizeInfo');
  }
  return {
    outerWidth: pldSizeInfo.imageWidth, // 外框宽度
    outerHeight: pldSizeInfo.imageHeight, // 外框高度
    left: pldSizeInfo.rect[0],
    top: pldSizeInfo.rect[1], // 内框距离外框上边距
    width: pldSizeInfo.rect[2], // 内框宽度
    height: pldSizeInfo.rect[3]
  };

  // return {
  //   outerWidth: 57, // 外框宽度
  //   outerHeight: 89, // 外框高度
  //   top: 11, // 内框距离外框上边距
  //   left: 5.5,
  //   width: 46, // 内框宽度
  //   height: 62,
  //   name: '拍立得尺寸'
  // };
}

/**
 * 检查图片是否颜色单一（异常检测）
 * 判断图片所有像素是否颜色相同，用于检测图片是否异常
 *
 * @param {string} imageSrc - 图片源地址（URL 或 Base64）
 * @returns {Promise<number>} 返回检测结果：1-有效，-1-无效（颜色单一）
 *
 * @description
 * 检测逻辑：
 * 1. 将图片缩放到 100x100 以内
 * 2. 绘制到 canvas
 * 3. 获取像素数据
 * 4. 每隔 128 个像素采样一次
 * 5. 对比所有采样点的颜色
 * 6. 如果所有颜色相同，说明图片异常
 *
 * 使用场景：
 * - 检测上传的图片是否损坏
 * - 检测图片处理后是否异常
 */
function checkImageIsSameColor(imageSrc) {
  return new Promise(async (resolve, reject) => {
    // 创建一个新的Image对象
    let img = new Image();

    // 设置图片源为你的图片路径
    img.src = imageSrc;

    // 当图片加载完成后执行的函数
    img.onload = function () {
      // 获取Canvas元素和2D渲染上下文
      var canvas = document.createElement('canvas');
      let ctx = canvas.getContext('2d');

      let maxSize = 100;
      let info = mwx.calcMaxSize(img.width, img.height, maxSize, maxSize);
      canvas.width = info.width;
      canvas.height = info.height;
      canvas.style.width = info.width + 'px';
      canvas.style.height = info.height + 'px';

      // 在Canvas上绘制图片
      ctx.drawImage(img, 0, 0, info.width, info.height);

      // 获取图片的像素数据
      let imageData = ctx.getImageData(0, 0, info.width, info.height);
      let data = imageData.data;

      // 检查每个像素是否为黑色
      let prevColor = '';
      let isValid = false;

      data.some((i) => {});

      for (let i = 0; i < data.length; i += 128) {
        let color = `rgb(${data[i]},${data[i + 1]},${data[i + 2]})`;
        if (!prevColor) {
          prevColor = color;
        }
        if (prevColor != color) {
          isValid = true;
        }
      }
      resolve(isValid ? 1 : -1);

      canvas = null;
      ctx = null;
      if (!isValid) {
        mwx.slog.info(`checkImageIsSameColor isValid=${isValid}`);
      }
    };
    img.onerror = function () {
      resolve(-1);
    };
  });
}

function getFileMimeType(file) {
  return new Promise((resolve, reject) => {
    // 创建一个FileReader实例
    const reader = new FileReader();

    // 定义读取完成后的回调函数

    reader.onloadend = async function () {
      const arrayBuffer = this.result; // 结果是一个ArrayBuffer对象

      let miniType = await FileType.fromBuffer(arrayBuffer);

      resolve(miniType ? miniType.ext : '');
      // mwx.slog.log('miniType=', miniType);
    };

    // 开始读取File的内容为ArrayBuffer
    reader.readAsArrayBuffer(file);

    reader.onerror = function (event) {
      mwx.slog.error('FileReader encountered an error:', event);
      resolve('jpg'); // 失败按照jpg处理
    };
  });
}

/**
 * 自动模式，根据将图片列表按照模板进行排列，从左到右从上到下进行排列。重新排列,模板的ratio和子模板的itemList会发生改变
 * @param {*} template 模板信息
 * @returns
 */
function createAutoModeTemplate(template, imageInfoList) {
  // 图片类别
  let imageInfoListCopy = JSON.parse(JSON.stringify(imageInfoList));
  //
  let baseRowHeight = 1;
  let baseRowWidth = 1;
  let everyRowInfoList = []; // 每一行的信息
  template.layout.forEach((row) => {
    let rowList = [];
    let rowWidth = 0;
    // 每行中的高度信息都是一样的,计算每个单元格所占的宽度
    row.forEach((column) => {
      let info = imageInfoListCopy.shift();
      let width = baseRowHeight * (info.width / info.height);
      rowList.push({
        height: baseRowHeight,
        width
      });
      rowWidth += width;
    });
    everyRowInfoList.push({
      rowWidth, // 一行的总宽度
      rowHeight: baseRowHeight, // 一行的高度
      rowList // 一行中各个单元格的信息
    });
  });
  // everyRowInfoList中的的每行的高度都是baseRowHeight,宽度可能不同。
  // 需要按照比例重新调整，使得宽度一致，高度自适应。
  let everyColListInfo = []; //
  let totalColHeight = 0;
  everyRowInfoList.forEach((v) => {
    let colHeight = baseRowWidth * (v.rowHeight / v.rowWidth);
    everyColListInfo.push({
      colWidth: baseRowWidth,
      colHeight: colHeight
    });
    totalColHeight += colHeight;
  });
  // 整个模板的比例
  let ratio = baseRowWidth / totalColHeight;
  // 构造template.js中的itemList结构
  let itemList = [];
  // Y轴上的偏移量
  let top = 0;
  template.layout.forEach((row, rowIndex) => {
    // 当前行的高度占总高度的百分比
    let percent_height =
      (everyColListInfo[rowIndex].colHeight / totalColHeight) * 100;
    // x轴上的偏移量
    let left = 0;
    // 当前行的总宽度
    let rowWidth = everyRowInfoList[rowIndex].rowWidth;
    everyRowInfoList[rowIndex].rowList.forEach((column, columnIndex) => {
      let percent_width = (column.width / rowWidth) * 100;
      itemList.push({ top, left, percent_width, percent_height });
      left += percent_width;
    });
    top += percent_height;
  });
  // mwx.slog.log('itemList', itemList);
  return {
    ...template,
    ratio,
    itemList
  };
}

function createHorizontalAutoModeTemplate(template, imageInfoList) {
  // 图片类别
  let imageInfoListCopy = JSON.parse(JSON.stringify(imageInfoList));
  let baseRowHeight = 1;
  let baseRowWidth = 1;
  let everyRowInfoList = []; // 每一行的信息
  template.layout.forEach((row) => {
    let rowList = [];
    let rowHeight = 0;
    // 每行中的高度信息都是一样的,计算每个单元格所占的宽度
    row.forEach((column) => {
      let info = imageInfoListCopy.shift();
      let height = baseRowWidth * (info.height / info.width);
      rowList.push({
        width: baseRowWidth,
        height
      });
      rowHeight += height;
    });
    everyRowInfoList.push({
      rowHeight, // 一行的总宽度
      rowWidth: baseRowWidth, // 一行的高度
      rowList // 一行中各个单元格的信息
    });
  });
  // everyRowInfoList中的的每行的高度都是baseRowHeight,宽度可能不同。
  // 需要按照比例重新调整，使得宽度一致，高度自适应。
  let everyColListInfo = []; //
  let totalColWidth = 0;
  everyRowInfoList.forEach((v) => {
    let colWidth = baseRowHeight * (v.rowWidth / v.rowHeight);
    everyColListInfo.push({
      colHeight: baseRowHeight,
      colWidth: colWidth
    });
    totalColWidth += colWidth;
  });
  // 整个模板的比例
  let ratio = totalColWidth / baseRowHeight;
  // 构造template.js中的itemList结构
  let itemList = [];
  // Y轴上的偏移量
  let left = 0;
  template.layout.forEach((row, rowIndex) => {
    // 当前行的高度占总高度的百分比
    let percent_width =
      (everyColListInfo[rowIndex].colWidth / totalColWidth) * 100;
    // x轴上的偏移量
    let top = 0;
    // 当前行的总宽度
    let rowHeight = everyRowInfoList[rowIndex].rowHeight;
    everyRowInfoList[rowIndex].rowList.forEach((column, columnIndex) => {
      let percent_height = (column.height / rowHeight) * 100;
      itemList.push({ top, left, percent_width, percent_height });
      top += percent_height;
    });
    left += percent_width;
  });
  // mwx.slog.log('itemList', itemList);
  return {
    ...template,
    ratio,
    itemList
  };
}

import heic2any from 'heic2any';
/*
  fileList: 文件列表，需要转换为图片blobUrl（需要进行格式转换、压缩、旋转等操作）
  template: 模板信息种的itemList信息包含显示的每个位置图片比例信息，需要对比这个比例和原图的比例来判断是否需要进行旋转操作
  addImageIndex: 添加图片的索引，从addImageIndex位置开始添加图片。注：index>addImageIndex时，需判断这个canAddImageStatusList位置是否为true（默认图）。是默认图才表示可以添加图片
  canAddImageStatusList：是否可以添加图片的状态列表，true表示默认图，可以添加图片
*/
async function handleTemplateImageList({ fileList, template, addIndexList }) {
  // 返回一个任务队列
  let startTime = new Date().getTime();
  let errMsg = '';
  let taskList = fileList.map((file, index) => {
    return async () => {
      let { blobUrl } = await transformFileToImage(file);
      try {
        let imageInfo = await mwx.getImageInfo(blobUrl, 1);
        let imageRatio = imageInfo.width / imageInfo.height;
        let rotateImageRatio = imageInfo.height / imageInfo.width;
        let subItemTemplate = template.itemList[addIndexList[index]];
        let itemRatio =
          (template.ratio * subItemTemplate.percent_width) /
          subItemTemplate.percent_height;
        // 计算原图片和旋转后的图片哪个更接近模板中指定位置的比例
        if (
          Math.abs(imageRatio - itemRatio) >
          Math.abs(rotateImageRatio - itemRatio)
        ) {
          // 旋转
          let sizeInfo = getPbRotateImageSize({
            width: imageInfo.height,
            height: imageInfo.width
          });
          let r = await rotateImage({
            imageSrc: blobUrl,
            canvasWidth: sizeInfo.width,
            canvasHeight: sizeInfo.height,
            destWidth: sizeInfo.width,
            destHeight: sizeInfo.height,
            rotate: 90
          });
          blobUrl = r;
        }
      } catch (error) {
        mwx.slog.error('图片转换blobUrl失败：');
        mwx.slog.capture(error);
        errMsg = mwx.failReasonMap.fileToUrlFail;
        throw new Error(mwx.failReasonMap.fileToUrlFail);
      }
      return blobUrl;
    };
  });
  let proList = await mwx.limitConcurrency(taskList, 5);
  if (errMsg) {
    mwx.Toast(`${proList.length ? '部分' : ''}图片格式不支持，已自动删除`);
  }
  proList = proList.filter((v) => v);
  mwx.logTime(
    'handleTemplateImageList耗时',
    new Date().getTime() - startTime + 'ms'
  );
  return proList;
}

// 拼版：旋转图片的宽高最短边不超过1000
function getPbRotateImageSize({ width, height, minSize = 1000 }) {
  if (width > minSize || height > minSize) {
    let ratio = width / height;
    if (ratio >= 1) {
      return {
        height: minSize,
        width: Math.round(minSize * ratio)
      };
    } else {
      return {
        width: minSize,
        height: Math.round(minSize / ratio)
      };
    }
  } else {
    return {
      width,
      height
    };
  }
}

/**
 * Transforms a file into an image.
 * @param {File} file - The file to be transformed.
 * @returns {Promise<string>} 图片链接（ blobUrl 或 base64Url）
 */
async function transformFileToImage(file) {
  let blobUrl = '';
  let fileName = file ? file.name || '' : '';
  let miniType = 'jpg';
  let isConverted = false; // 新增：标记是否进行了格式转换

  try {
    miniType = await getFileMimeType(file);
  } catch (error) {
    mwx.slog.error(
      `getFileMimeType fail:fileName=${fileName},size=${
        file && file.size
      },isFile=${file instanceof File}`
    );
    // 处理错误
  }

  // if (index == 1) {
  //   throw new Error(mwx.failReasonMap.convertFail);
  // }
  let isCameraImages = /\.cr2/gi.test(file.name); // 目前cr2格式支持转换，其余格式未进行测试
  // APNG 基于 PNG 编码，后续上传和图片处理按 PNG 口径处理
  if (miniType === 'apng') {
    miniType = 'png';
  }
  let isWhiteListImages = /^(jpg|png|apng|jpeg|heic|webp)$/gi.test(miniType);
  let fileNameParseList = fileName.split('.');
  let fileInfo = '';
  if (file && file.size) {
    fileInfo = `name=${file.name},mb=${Math.round(
      file.size / 1024 / 1024
    )},type=${file.type}`;
  }
  mwx.slog.log(
    `[transformFileToImage]:fileInfo=${fileInfo},miniType=${miniType},isFile=${
      file instanceof File
    }`
  );

  // 非白名单之内默认转换为jpg
  if (!isWhiteListImages) {
    mwx.slog.info('非白名单之内转换为jpg,miniType=' + miniType);
    miniType = 'jpg';
    isWhiteListImages = true;
    isConverted = true; // 标记为已转换
  }

  // 错误的文件后缀需要重命名，否则会导致上传图片报错
  try {
    if (
      fileNameParseList[1] &&
      fileNameParseList[1].toLowerCase() != miniType &&
      ['jpg', 'png', 'jpeg'].indexOf(fileNameParseList[1]) == -1
    ) {
      file = new File([file], fileNameParseList[0] + '.' + miniType, {
        type: file.type
      });
      mwx.slog.log(`rename file,size=${file.size},name=${file.name}`);
      isConverted = true; // 标记为已转换
    }
  } catch (error) {
    mwx.slog.error(error);
    // 处理错误
  }

  // if(miniType!=file.name)

  if (miniType == 'webp' && mwx.isSupprtWebp) {
    blobUrl = await mwx.fileToBase64(file);
  } else if (miniType == 'heic') {
    var blob = new Blob([file], { type: 'image/heic' });
    try {
      let r = await heic2any({
        blob,
        toType: 'image/jpeg'
      });
      file = r;
      isConverted = true; // 标记为已转换
    } catch (error) {
      if (
        error &&
        error.message &&
        error.message.indexOf('ERR_USER Image is already browser readable') > -1
      ) {
        // 不需要转换的图片
      } else {
        mwx.slog.error(`heic2any error:name=${fileName}`);
        mwx.slog.capture(error);
        throw new Error(mwx.failReasonMap.convertFail);
      }
    }
  } else if (isCameraImages || !isWhiteListImages) {
    try {
      file = await mwx.convertRawCameraImage(file);
      isConverted = true; // 标记为已转换
    } catch (error) {
      mwx.slog.error(
        `convertRawCameraImage error:name=${fileName},miniType=${miniType}`
      );
      mwx.slog.capture(error);
      throw new Error(mwx.failReasonMap.imageTypeError);
    }
  }

  if (!blobUrl) {
    try {
      blobUrl = mwx.fileToBlobUrl(file);
      console.log('transformFileToImage fileToBlobUrl', file.type, blobUrl);
    } catch (error) {
      mwx.slog.error('fileToBlobUrl error', error);
      // 处理错误
    }
  }

  // mwx.slog.log('transformFileToImage return blobUrl', blobUrl);
  try {
    await mwx.getImageInfo(blobUrl, 1);
  } catch (error) {
    mwx.slog.error(
      `图片转换blobUrl失败，尝试转换base64：fileName=${fileName},blobUrl=${String(
        blobUrl || ''
      ).slice(0, 200)}`
    );
    try {
      blobUrl = await mwx.fileToBase64(file);
      await mwx.getImageInfo(blobUrl, 1);
      mwx.slog.info('图片转换base64成功');
    } catch (error) {
      mwx.slog.error('fileToBase64 失败', error);
      // 处理错误
      mwx.slog.error(`图片转换base64失败`);
      throw new Error(mwx.failReasonMap.fileToUrlFail);
    }
  }

  return { blobUrl, isConverted, miniType }; // 新增 isConverted 字段
}

// https://www.showdoc.com.cn/2272359968911745/10188142564529487
// 拍立得的skuType

let chuXueCache = {};
let roundChuXueCache = {};

function createChuXue({
  canvasWidth,
  canvasHeight,
  top,
  left,
  interval = 30,
  color = '#E60012',
  angle = 135
}) {
  if (canvasWidth < 1000 || canvasHeight < 1000) {
    let w = canvasWidth;
    canvasWidth = 1000;
    let ratio = 1000 / w;
    top = Math.round(top * ratio);
    left = Math.round(left * ratio);
    canvasHeight = Math.round(canvasWidth / (w / canvasHeight));
  }

  try {
    let cacheKey = `canvasWidth=${canvasWidth},canvasHeight=${canvasHeight},top=${top},left=${left},interval=${interval},angle=${angle}`;
    if (chuXueCache[cacheKey]) {
      return Promise.resolve(chuXueCache[cacheKey]);
    }
    var canvas = document.createElement('canvas');
    // 设置画布的宽高
    canvas.width = canvasWidth;
    canvas.height = canvasHeight;
    top = Math.round(top);
    left = Math.round(left);
    var ctx = canvas.getContext('2d');
    canvasHeight = canvas.height;

    // 设置红色斜线样式
    ctx.strokeStyle = color;
    ctx.lineWidth = 2;
    ctx.lineCap = 'square';

    // 先在整个canvas上铺满斜线
    // 根据角度计算斜线的绘制方式
    const radians = (angle * Math.PI) / 180;
    const cos = Math.cos(radians);
    const sin = Math.sin(radians);

    // 计算斜线的长度，确保能覆盖整个canvas
    const lineLength =
      Math.sqrt(canvasWidth * canvasWidth + canvasHeight * canvasHeight) * 2;

    // 绘制斜线
    for (let x = -lineLength; x < canvasWidth + lineLength; x += interval) {
      ctx.beginPath();
      const startX = x;
      const startY = 0;
      const endX = startX + lineLength * cos;
      const endY = startY + lineLength * sin;
      ctx.moveTo(startX, startY);
      ctx.lineTo(endX, endY);
      ctx.stroke();
    }

    // 清空中间区域 (left, top) 到 (canvasWidth-left, canvasHeight-top)
    ctx.clearRect(left, top, canvasWidth - left * 2, canvasHeight - top * 2);

    // 在中间区域绘制红色矩形框
    ctx.strokeStyle = color;
    // ctx.lineWidth = 1;
    ctx.strokeRect(left, top, canvasWidth - left * 2, canvasHeight - top * 2);

    ctx.strokeRect(0, 0, canvasWidth, canvasHeight);

    // 使用 toBlob 生成 blobUrl 而不是 base64
    return new Promise((resolve) => {
      canvas.toBlob((blob) => {
        if (blob) {
          const blobUrl = URL.createObjectURL(blob);
          chuXueCache[cacheKey] = blobUrl;
          resolve(blobUrl);
        } else {
          // 降级处理：如果 blob 生成失败，返回 base64
          const base64Url = canvas.toDataURL('image/png');
          chuXueCache[cacheKey] = base64Url;
          resolve(base64Url);
        }
      }, 'image/png');
    });
  } catch (error) {
    mwx.slog.error(error);
    // 处理错误
    return require('@/assets/image/6.png');
  }
}

function createRoundChuXue({
  radius,
  roundBorder,
  interval = 30,
  color = '#E60012',
  angle = 135
}) {
  if (radius < 1000) {
    let originalRadius = radius;
    radius = 1000;
    let ratio = 1000 / originalRadius;
    roundBorder = Math.round(roundBorder * ratio);
  }

  try {
    let cacheKey = `radius=${radius},roundBorder=${roundBorder},interval=${interval},angle=${angle}`;
    if (roundChuXueCache[cacheKey]) {
      return Promise.resolve(roundChuXueCache[cacheKey]);
    }

    var canvas = document.createElement('canvas');
    // 设置画布的宽高为直径
    canvas.width = radius * 2;
    canvas.height = radius * 2;
    roundBorder = Math.round(roundBorder);
    var ctx = canvas.getContext('2d');

    // 设置红色斜线样式
    ctx.strokeStyle = color;
    ctx.lineWidth = 2;
    ctx.lineCap = 'square';

    // 先在整个canvas上铺满斜线
    // 根据角度计算斜线的绘制方式
    const radians = (angle * Math.PI) / 180;
    const cos = Math.cos(radians);
    const sin = Math.sin(radians);

    // 计算斜线的长度，确保能覆盖整个canvas
    const lineLength = radius * 2 * Math.sqrt(2);

    // 设置裁剪区域为外圆
    ctx.beginPath();
    ctx.arc(radius, radius, radius, 0, 2 * Math.PI);
    ctx.clip();

    // 绘制斜线（仅在裁剪区域内显示）
    for (let x = -lineLength; x < radius * 2 + lineLength; x += interval) {
      ctx.beginPath();
      const startX = x;
      const startY = 0;
      const endX = startX + lineLength * cos;
      const endY = startY + lineLength * sin;
      ctx.moveTo(startX, startY);
      ctx.lineTo(endX, endY);
      ctx.stroke();
    }

    // 清空中心圆形区域，保留圆环
    ctx.globalCompositeOperation = 'destination-out';
    ctx.beginPath();
    ctx.arc(radius, radius, radius - roundBorder, 0, 2 * Math.PI);
    ctx.fill();

    // 恢复合成模式
    ctx.globalCompositeOperation = 'source-over';

    // 绘制外圆边框
    ctx.strokeStyle = color;
    ctx.beginPath();
    ctx.arc(radius, radius, radius, 0, 2 * Math.PI);
    ctx.stroke();

    // 绘制内圆边框
    ctx.strokeStyle = color;
    ctx.beginPath();
    ctx.arc(radius, radius, radius - roundBorder, 0, 2 * Math.PI);
    ctx.stroke();

    // 使用 toBlob 生成 blobUrl 而不是 base64
    return new Promise((resolve) => {
      canvas.toBlob((blob) => {
        if (blob) {
          const blobUrl = URL.createObjectURL(blob);
          roundChuXueCache[cacheKey] = blobUrl;
          resolve(blobUrl);
        } else {
          // 降级处理：如果 blob 生成失败，返回 base64
          const base64Url = canvas.toDataURL('image/png');
          roundChuXueCache[cacheKey] = base64Url;
          resolve(base64Url);
        }
      }, 'image/png');
    });
  } catch (error) {
    mwx.slog.error(error);
    // 处理错误
    return require('@/assets/image/6.png');
  }
}

// 输入
function getFontWidth({ text, fontSize }) {
  const span = document.createElement('span');
  span.style.position = 'fixed';
  span.style.left = '999px';
  span.style.lineHeight = 1;
  span.innerText = text;
  span.style.fontSize = fontSize + 'px';
  document.body.appendChild(span);
  //
  let r = {
    width: span.offsetWidth,
    height: span.offsetHeight
  };
  // document.body.removeChild(span);
  return r;
}

function getAddTextFontInfo({
  showWidth,
  polaroidWidth,
  maxTextCount = 15,
  inch
}) {
  // 获取单个字体宽高
  let singleFontInfo = getFontWidth({
    text: '我',
    fontSize: 12
  });
  let max = 3000; // 防止字体过大
  let isMinify = false;
  let ratio = max / polaroidWidth;
  if (polaroidWidth > max) {
    isMinify = true;
    polaroidWidth = max;
    showWidth = Math.round(showWidth * ratio);
  }
  let showRatio = showWidth / polaroidWidth; // 展示宽度与实际宽度的比例
  let fontHeight = Math.round(polaroidWidth / maxTextCount); // font高度为rect的1/maxTextCount
  let showFontHeight = fontHeight * showRatio; // 视觉上的font高度
  let heightScale = (showFontHeight / singleFontInfo.height).toFixed(3); // 展示文字高度和12px文字高度的比例
  let fontScale = heightScale / showWidth; // 实际字体大小scale= fontScale * showWidth

  // 使用canvas绘制text,并导出图片
  // function drawTextToCanvas({ text }) {
  //   const canvas = document.createElement('canvas');
  //   const ctx = canvas.getContext('2d');
  //   let fontSize = Math.round((12 * heightScale) / showRatio);
  //   fontSize = 30;
  //   ctx.font = `${fontSize}px sans-serif`;
  //
  //   let canvasWidth = ctx.measureText(text).width;
  //   let canvasHeight = fontHeight;
  //   canvas.width = canvasWidth;
  //   canvas.height = canvasHeight;
  //   ctx.textAlign = 'center';
  //   ctx.textBaseline = 'middle';
  //   ctx.fillText(text, canvasWidth / 2, canvasHeight / 2);
  //   return canvas.toDataURL();
  // }
  // let r = drawTextToCanvas({ text, fontScale });
  // mwx.downloadFile(r);

  if (isMinify) {
    fontScale = fontScale * ratio;
  }

  return {
    fontHeight,
    fontScale,
    singleFontInfo,
    showRatio
  };
}

function handerGoodsDetail({
  sku, // skuList中子订单对应的sku信息
  goodsDetail, // 所有订单的goodsDetail [{list,outerOiId},{list,outerOiId}]
  lifeSubIndex, // 当前编辑的子订单的索引（第几次上传，总共有qty次）
  outerOiId, // 需要更新的子订单的id
  updatedList // 子订单需要更新的list数据
}) {
  let newList = [];
  // 需要拆分上传的数据列表[[lifeSubIndex=0的数据数据],[lifeSubIndex=1的列表数据]]
  // 一个生活照sku购买了多次（会拆分为多个子订单进行处理）,更新指定子订单（lifeSubIndex对应）的列表

  // 在订单中筛选出当前编辑的子订单的index
  let updateIndex = goodsDetail.findIndex((v) => v.outerOiId === outerOiId);
  // 当前子订单的数据
  let currentOuterIdData = goodsDetail[updateIndex];
  let prevGroupList = [];
  // 当前子订单是否上传过数据
  if (
    currentOuterIdData &&
    currentOuterIdData.list &&
    currentOuterIdData.list.length
  ) {
    // 如果当前sku的数量大于1，会分次进行上传，每次上传template.bgCount张图片。总共上传sku.qty次。
    // 当前上传的是第lifeSubIndex次
    prevGroupList =
      sku.qty > 1
        ? divideListByCount({
            sku,
            list: currentOuterIdData.list,
            divideCount: sku.template.bgCount
          }) || []
        : [];
  }
  if (prevGroupList && prevGroupList.length) {
    prevGroupList[lifeSubIndex] = [...updatedList]; //  第lifeSubIndex次上传的数据
    prevGroupList.forEach((arr) => {
      // 合并所有的数据
      newList = [...newList, ...arr];
    });
  } else {
    newList = [...updatedList];
  }

  // 当前子订单更新的数据
  let updateData = {
    outerOiId,
    list: [...newList]
  };
  // goodsDetail包含所有子订单的数据，更新指定子订单的数据
  if (updateIndex == -1) {
    // 不存在则新增
    goodsDetail.push(updateData);
  } else {
    // 存在则更新
    goodsDetail[updateIndex] = updateData;
  }
  return goodsDetail;
}

function filterString(input) {
  // 定义一个正则表达式，用于匹配中文、英文、数字和常用标点符号
  const regex = /[^[\u4e00-\u9fa5a-zA-Z0-9!"#$%&'()*+,，。！？\-./:;<=>?@[\\\]^_`{|}~\s]/g;

  // 使用正则表达式替换不符合条件的字符为空字符串
  const filtered = input.replace(regex, '');

  return filtered;
}

function getLifePhotoTextHeight({ inch, showWidth, shadow = 0 }) {
  // let base = 250;
  // let ratio = showHeight / paperHeight;
  // let r = base * ratio;
  let textHeight;
  let fontScale;
  let fontHeight;
  // 获取单个字体宽高
  let singleFontInfo = getFontWidth({
    text: '我',
    fontSize: 12
  });

  // 5寸尺寸 12.7cm*8.9cm
  // 6寸尺寸 15.2cm*10.2cm
  // 7寸 17.8cm*12.7cm
  // 8寸 20.3cm*15.2cm
  // 五六寸距离底部 6 mm，字高 6mm；七寸八寸距离底部 8mm，字高 7mm

  // 尺寸对应的宽度（较短边）
  let inchWidthMap = {
    5: 8.9,
    6: 10.2,
    7: 12.7,
    8: 15.2
  };
  let val = inchWidthMap[inch] || inchWidthMap[5];
  if (inch == 7 || inch == 8) {
    fontHeight = (showWidth / val) * 0.8; // 七寸八寸距离底部 8mm，字高 7mm
    fontScale = fontHeight / singleFontInfo.height;
    textHeight = (fontHeight * (8 * 2 + 7)) / 7 + shadow * 2;
  } else {
    fontHeight = (showWidth / val) * 0.6; // 上下距离底部6mm，字高6mm，刚好3倍
    fontScale = fontHeight / singleFontInfo.height;
    textHeight = (fontHeight * (0.6 * 2 + 0.6)) / 0.6 + shadow * 2;
  }
  fontScale = fontScale / showWidth;

  return {
    textHeight,
    fontScale,
    fontHeight
  };
}

function createClipImage({
  radius,
  canvasWidth,
  canvasHeight,
  background,
  offsetX,
  offsetY
}) {
  const width = canvasWidth;
  const height = canvasHeight;

  // 创建 canvas 并设置大小
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');

  // 填充背景颜色
  ctx.fillStyle = background;
  ctx.fillRect(0, 0, width, height);

  // 创建预留 offsetX 和 offsetY 的圆角路径
  ctx.beginPath();
  ctx.moveTo(offsetX + radius, offsetY);
  ctx.lineTo(width - offsetX - radius, offsetY);
  ctx.arcTo(
    width - offsetX,
    offsetY,
    width - offsetX,
    offsetY + radius,
    radius
  );
  ctx.lineTo(width - offsetX, height - offsetY - radius);
  ctx.arcTo(
    width - offsetX,
    height - offsetY,
    width - offsetX - radius,
    height - offsetY,
    radius
  );
  ctx.lineTo(offsetX + radius, height - offsetY);
  ctx.arcTo(
    offsetX,
    height - offsetY,
    offsetX,
    height - offsetY - radius,
    radius
  );
  ctx.lineTo(offsetX, offsetY + radius);
  ctx.arcTo(offsetX, offsetY, offsetX + radius, offsetY, radius);
  ctx.closePath();

  // 应用裁剪
  ctx.clip();

  // 清空裁剪区域，设置为透明
  ctx.clearRect(offsetX, offsetY, width - 2 * offsetX, height - 2 * offsetY);

  let r = canvas.toDataURL('image/png');
  console.log('r', r);
  return r;
}

function base64ToBlobUrl(base64Data) {
  // 检查并分离 base64 数据前缀
  const parts = base64Data.split(',');
  const contentType = parts[0].match(/:(.*?);/)[1]; // 提取 MIME 类型
  const rawBase64 = parts[1]; // 获取真正的 base64 数据

  // 解码 base64 数据为 Blob
  const byteCharacters = atob(rawBase64);
  const byteNumbers = new Array(byteCharacters.length)
    .fill(null)
    .map((_, i) => byteCharacters.charCodeAt(i));
  const byteArray = new Uint8Array(byteNumbers);
  const blob = new Blob([byteArray], { type: contentType });

  // 创建 Blob URL
  return URL.createObjectURL(blob);
}

function _checkImageTransparentPercent(imageSrc) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.setAttribute('crossOrigin', 'Anonymous');
    img.onload = function () {
      const maxWidth = 200;
      const maxHeight = 200;

      // 缩放图片以适应 200x200
      const scale = Math.min(maxWidth / img.width, maxHeight / img.height, 1);
      const canvas = document.createElement('canvas');
      canvas.width = Math.floor(img.width * scale);
      canvas.height = Math.floor(img.height * scale);

      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

      // 获取像素数据
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const data = imageData.data;
      let transparentPixelCount = 0;

      for (let i = 0; i < data.length; i += 4) {
        const alpha = data[i + 3]; // 获取 alpha 通道值
        if (alpha === 0) {
          transparentPixelCount++;
        }
      }

      const totalPixels = canvas.width * canvas.height;
      const transparentRatio = (transparentPixelCount / totalPixels) * 100;
      resolve(transparentRatio);
    };

    img.onerror = function (err) {
      console.error('checkImageTransparentPercent error', err);

      reject(err);
    };
    img.src = imageSrc;
  });
}

const checkImageTransparentPercent = createCachedMethod(
  _checkImageTransparentPercent,
  { maxSize: 30 }
);

// 检查图片是否需要抠图，如果图片中有透明像素超过 5%，则认为需要抠图
async function checkIfRetouchImage(image) {
  let startTime = new Date().getTime();
  let percent = await checkImageTransparentPercent(image);
  mwx.logTime(
    'checkIfRetouchImage耗时',
    new Date().getTime() - startTime + 'ms'
  );
  if (percent > 5) {
    return true;
  } else {
    return false;
  }
}

function getTemplateBgColor(query, imageSrc) {
  let prevTemplateInfo = mwx.Store.get('lifePb:lifePbMap') || {};
  return prevTemplateInfo[imageSrc] || '';
}

function getOddCountBgColor(query) {
  let prevTemplateInfo = mwx.Store.get('lifePb:lifePbMap') || {};
  let uploadPhotoList = mwx.getPhotoList(query) || [];
  let colorList = uploadPhotoList
    .filter((v) => v.originImageSrc)
    .map((v) => {
      return {
        color: prevTemplateInfo[v.originImageSrc],
        count: v.count
      };
    })
    .filter((v) => v.color);

  let colorCountMap = {};
  colorList.forEach((v) => {
    if (colorCountMap[v.color]) {
      colorCountMap[v.color] += v.count;
    } else {
      colorCountMap[v.color] = v.count;
    }
  });
  let bgColor = [];
  Object.keys(colorCountMap).forEach((key) => {
    if (colorCountMap[key] % 2 != 0) {
      bgColor.push(key);
    }
  });

  return bgColor;
}

function imageUrlToBase64(url) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('GET', url, true);
    xhr.responseType = 'blob'; // 设置响应类型为Blob

    xhr.onload = function () {
      if (this.status === 200) {
        const reader = new FileReader();

        // 当FileReader完成读取时触发
        reader.onloadend = function () {
          resolve(reader.result); // 成功时解析Promise
        };

        reader.onerror = function () {
          reject(new Error('Error reading file'));
        };

        reader.readAsDataURL(xhr.response); // 开始读取作为data URL的Blob对象
      } else {
        reject(new Error('Image download failed with status: ' + this.status));
      }
    };

    xhr.onerror = function () {
      reject(new Error('Image download failed'));
    };

    xhr.send(); // 发送请求
  });
}

export function convertImageToBlobUrl(imageUrl) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('GET', imageUrl, true);
    xhr.responseType = 'blob'; // 关键：指定响应类型为Blob
    xhr.onload = function () {
      if (xhr.status === 200) {
        const blob = xhr.response;
        const blobUrl = URL.createObjectURL(blob);
        resolve(blobUrl);
      } else {
        reject('图片加载失败');
        mwx.log.error(
          'convertImageToBlobUrl:图片加载失败,imageUrl=' + imageUrl
        );
        mwx._getImageErrorCodeInfo(imageUrl);
      }
    };
    xhr.onerror = function () {
      reject('图片加载失败');
      mwx.log.error('convertImageToBlobUrl:图片加载失败,imageUrl=' + imageUrl);
      mwx._getImageErrorCodeInfo(imageUrl);
    };
    xhr.send();
  });
}

function getToolList(typeList, isFooter = false) {
  let arr = [
    {
      type: mwx.constant.tool_changeImage,
      text: '换一张',
      image: require('@/assets/image/xb_photo_icon_anotherpic@2x.png'),
      footerTypeImage: require('@/assets/image/tool-change-icon.png')
    },
    {
      type: mwx.constant.tool_editImage,
      text: '编辑图片',
      image: require('@/assets/image/icon_edit.png'),
      footerTypeImage: require('@/assets/image/tool-edit-icon.png')
    },
    {
      type: mwx.constant.tool_rotate,
      text: '旋转',
      image: require('@/assets/image/xb_photo_icon_rotate@2x.png')
    },
    {
      type: mwx.constant.tool_changeSize,
      text: '改尺寸',
      image: require('@/assets/image/change-size.png')
    },
    {
      type: mwx.constant.tool_changeHole,
      text: '改孔位',
      image: require('@/assets/image/changeHole@2x.png')
    },
    {
      type: mwx.constant.tool_manualRetouch,
      text: '抠细节',
      image: require('@/assets/image/cutout_cutout_icon_erase_s.png')
    },
    {
      type: mwx.constant.tool_retouch,
      text: '抠图',
      image: require('@/assets/image/retouch.png')
    },
    {
      type: mwx.constant.tool_showRetouch,
      text: '抠图',
      image: require('@/assets/image/retouch.png')
    },
    {
      type: mwx.constant.tool_noRetouch,
      text: '原图',
      image: require('@/assets/image/retouch_un.png'),
      selectedImage: require('@/assets/image/retouch_un_s.png')
    },
    {
      type: mwx.constant.tool_original,
      text: '原图',
      image: require('@/assets/image/retouch_un.png'),
      selectedImage: require('@/assets/image/retouch_un_s.png')
    },
    {
      type: mwx.constant.tool_noRetouch1,
      text: '不抠图',
      image: require('@/assets/image/retouch_un.png'),
      selectedImage: require('@/assets/image/retouch_un_s.png')
    },
    {
      type: mwx.constant.tool_text,
      text: '文字',
      image: require('@/assets/image/vector.png'),
      image_s: require('@/assets/image/text_active.png')
    },
    {
      type: mwx.constant.tool_zoomin,
      text: '放大',
      image: require('@/assets/image/xb_photo_icon_zoomin@2x.png')
    },
    {
      type: mwx.constant.tool_zoomout,
      text: '缩小',
      image: require('@/assets/image/xb_photo_icon_zoomout@2x.png')
    },
    {
      type: mwx.constant.tool_headRetouch,
      text: '抠大头',
      image: require('@/assets/image/retouchHead.png'),
      selectedImage: require('@/assets/image/retouchHead_s.png')
    },
    {
      type: mwx.constant.tool_fullBodyRetouch,
      text: '抠全身',
      image: require('@/assets/image/fullBodyRetouch.png'),
      selectedImage: require('@/assets/image/fullBodyRetouch_s.png')
    },
    {
      type: mwx.constant.tool_outline,
      text: '描边',
      image: require('@/assets/image/outline.png')
    },
    {
      type: mwx.constant.tool_shape,
      text: '刀线',
      image: require('@/assets/image/shape.png')
    },
    {
      type: mwx.constant.tool_moveSocket,
      text: '插口',
      image: require('@/assets/image/icon_socket.png')
    },
    {
      type: mwx.constant.tool_changeBg,
      text: '背景',
      image: require('@/assets/image/icon_bg.png')
    },
    {
      type: mwx.constant.tool_whiteInk,
      text: '白墨',
      image: require('@/assets/image/whiteInk.png')
    },
    {
      type: mwx.constant.tool_mainBody,
      text: '主体',
      image: require('@/assets/image/mainBody.png'),
      selectedImage: require('@/assets/image/mainBody_s.png'),
      disabledImage: require('@/assets/image/mainBody_dis.png')
    },
    {
      type: mwx.constant.tool_clear,
      text: '清空',
      image: require('@/assets/image/clear.png'),
      selectedImage: require('@/assets/image/clear_s.png')
    },
    {
      type: mwx.constant.tool_upload,
      text: '上传',
      image: require('@/assets/image/upload.png'),
      selectedImage: require('@/assets/image/upload_s.png'),
      disabledImage: require('@/assets/image/upload_dis.png')
    },
    {
      type: mwx.constant.tool_default,
      text: '默认',
      unSelectedText: '重置',
      image: require('@/assets/image/reset.png'),
      selectedImage: require('@/assets/image/icon_default_s.png')
    },
    {
      type: mwx.constant.tool_template,
      text: '模板',
      image: require('@/assets/image/xb_photo_icon_template@2x.png')
    },
    {
      type: mwx.constant.tool_imageZindex,
      text: '图片置底',
      unSelectedText: '图片置顶',
      image: require('@/assets/image/imageZindex.png'),
      unSelectedImage: require('@/assets/image/imageZindex.png'),
      selectedImage: require('@/assets/image/imageZindex_s.png')
    },
    {
      type: mwx.constant.tool_setting,
      text: '设置',
      unSelectedText: '设置',
      image: require('@/assets/image/setting.png'),
      selectedImage: require('@/assets/image/setting.png')
    },
    {
      type: mwx.constant.tool_frontWhiteInk,
      text: '前景白墨',
      unSelectedText: '前景白墨',
      image: require('@/assets/image/front-white.png'),
      selectedImage: require('@/assets/image/front-white_s.png')
    },
    {
      type: mwx.constant.tool_reverse,
      text: '逆向',
      image: require('@/assets/image/reverse-white.png')
    },
    {
      type: mwx.constant.tool_scopeUv,
      text: '局部uv',
      image: require('@/assets/image/scope-uv.png')
    },
    {
      type: mwx.constant.tool_reversePrimerLayerAuto,
      text: '自动',
      image: require('@/assets/image/white-auto.png'),
      selectedImage: require('@/assets/image/white-auto-s.png')
    },
    {
      type: mwx.constant.tool_reversePrimerLayerFill,
      text: '铺满',
      image: require('@/assets/image/white-fill.png'),
      selectedImage: require('@/assets/image/white-fill-s.png')
    },
    {
      type: mwx.constant.tool_component,
      text: '组件',
      image: require('@/assets/image/icon-component.png')
    },
    {
      type: mwx.constant.tool_changeSize,
      text: '改尺寸',
      image: require('@/assets/image/sticker/changeSize.png')
    }
  ];

  let filterList = typeList
    .map((type) => arr.find((item) => item.type === type))
    .filter(Boolean);
  if (isFooter) {
    filterList = filterList.map((item) => {
      return {
        ...item,
        image: item.footerTypeImage || item.image
      };
    });
  }
  return filterList;
}

/**
 * 触发文件上传（图片选择）
 * 微信内优先使用 wx.chooseImage，失败时降级为原生 input[type=file]
 * 非微信环境直接使用原生 input[type=file]
 *
 * @param {Object} params - 参数对象
 * @param {string} [params.accept='image/jpg,image/png,image/jpeg,image/heic,image/webp'] - 文件类型
 * @param {boolean} [params.multiple=false] - 是否多选
 * @param {number} [params.count=9] - 微信环境下最大可选图片数量（multiple=true时生效）
 * @returns {Promise<File[]>} 返回选中的文件列表
 */
let isTriggerWxChooseImageFailed = true;
function triggerFileUpload({
  accept = 'image/jpg,image/png,image/jpeg,image/heic,image/webp',
  multiple = false,
  count = 1
} = {}) {
  // 微信环境：优先使用 wx.chooseImage，失败则降级到原生 input
  if (mwx.isWeChat() && !isTriggerWxChooseImageFailed) {
    mwx.log.info('》》》微信环境，先用微信api 选图《《《');
    return _triggerFileUploadInWeChat({ multiple, count }).catch((error) => {
      mwx.log.error(error);
      // 延迟触发原生 input，等待微信选图弹窗完全关闭
      return new Promise((resolve) => setTimeout(resolve, 0)).then(() => {
        mwx.log.info('微信api不可用，降级使用原生 input选图');
        isTriggerWxChooseImageFailed = true;
        return _triggerFileUploadNative({ accept, multiple });
      });
    });
  } else {
    // 非微信环境：使用原生 input[type=file]
    return _triggerFileUploadNative({ accept, multiple });
  }
}

/**
 * 原生 input[type=file] 方式上传文件
 *
 * @param {Object} params - 参数对象
 * @param {string} params.accept - 文件类型
 * @param {boolean} params.multiple - 是否多选
 * @returns {Promise<File[]>} 返回选中的文件列表
 */
function _triggerFileUploadNative({ accept, multiple }) {
  return new Promise((resolve, reject) => {
    mwx.log.info('非微信环境，使用原生 input选图');

    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = accept;
    fileInput.multiple = multiple;
    fileInput.style.display = 'none';

    fileInput.addEventListener('change', (e) => {
      resolve([...e.target.files]);
      mwx.log.info('原生 input选图成功,len=' + e.target.files.length);
      e.target.files = null;
      // 可选：用完移除元素
      setTimeout(() => fileInput.remove(), 100);
    });

    document.body.appendChild(fileInput);
    fileInput.click();
  });
}

/**
 * 微信环境下使用 wx.chooseImage 选图，并通过 wx.getLocalImgData 转为 File 对象
 * 如果 wx SDK 不可用或调用失败，抛出错误以便调用方降级到原生 input
 *
 * @param {Object} params - 参数对象
 * @param {boolean} params.multiple - 是否多选
 * @param {number} params.count - 最大可选图片数量
 * @returns {Promise<File[]>} 返回选中的文件列表
 * @throws {Error} 当 wx SDK 不可用或 API 调用失败时抛出错误
 */
async function _triggerFileUploadInWeChat({ multiple, count }) {
  // 检查 wx SDK 是否可用
  if (typeof wx === 'undefined') {
    throw new Error('wx SDK 未加载');
  }

  // 检查 wx.chooseImage 是否可用
  if (!wx.chooseImage) {
    throw new Error('wx.chooseImage 接口不可用');
  }

  try {
    // 调用 wx.chooseImage 选图
    const localIds = await new Promise((resolve, reject) => {
      let c = multiple ? (count > 9 ? 9 : count) : 1;
      console.log('选择图片数量1-9', c);
      wx.chooseImage({
        count: c,
        sizeType: ['original'],
        sourceType: ['album'],
        success: (res) => {
          console.log('《《使用wxChooseImage上传》》');
          resolve(res.localIds || []);
        },
        fail: (err) => {
          console.log('🚀 ~ _triggerFileUploadInWeChat ~ err:', err);
          // 用户取消选择返回空数组，SDK 错误则抛出
          const errMsg = (err && err.errMsg) || '';
          if (errMsg.indexOf('cancel') > -1 || errMsg.indexOf('cancel:') > -1) {
            resolve([]);
          } else {
            reject(new Error('wx.chooseImage 失败: ' + errMsg));
          }
        },
        cancel: () => resolve([])
      });
    });

    if (!localIds || localIds.length === 0) {
      return [];
    }

    // 将 localIds 转换为 File 对象
    const fileList = [];
    for (const localId of localIds) {
      try {
        const localData = await new Promise((resolveLocal, rejectLocal) => {
          // 检查 getLocalImgData 是否可用
          if (!wx.getLocalImgData) {
            rejectLocal(new Error('wx.getLocalImgData 接口不可用'));
            return;
          }
          wx.getLocalImgData({
            localId: localId,
            success: (res) => resolveLocal(res.localData),
            fail: (err) =>
              rejectLocal(
                new Error(
                  'wx.getLocalImgData 失败: ' + ((err && err.errMsg) || '')
                )
              )
          });
        });

        // 修复 iOS base64 格式：可能缺少 data:image 前缀
        let base64Data = localData;
        if (base64Data.indexOf('data:image') === -1) {
          base64Data = 'data:image/png;base64,' + base64Data;
        }
        // 修复 iOS 换行符问题
        base64Data = base64Data.replace(/\n/g, '');

        const file = mwx.base64ToFile(base64Data);
        fileList.push(file);
      } catch (error) {
        mwx.slog.error('wx.getLocalImgData fail:', error);
        // 任何一张图片转换失败，都抛出错误触发降级到原生 input
        throw new Error(
          'wx.getLocalImgData 失败，降级为原生 input: ' +
            (error && error.message)
        );
      }
    }

    return fileList;
  } catch (error) {
    throw error;
  }
}

export function cropAndZoomImage(imageUrl, zoomFactor) {
  return new Promise((resolve, reject) => {
    // 创建Image对象加载图片
    const img = new Image();
    img.crossOrigin = 'Anonymous'; // 处理跨域问题

    img.onload = function () {
      // 原始图片尺寸
      const originalWidth = img.width;
      const originalHeight = img.height;

      // 放大后的尺寸
      const zoomedWidth = originalWidth * zoomFactor;
      const zoomedHeight = originalHeight * zoomFactor;

      // 创建Canvas元素
      const canvas = document.createElement('canvas');
      canvas.width = originalWidth;
      canvas.height = originalHeight;
      const ctx = canvas.getContext('2d');

      // 计算绘制位置，使放大后的图片居中
      const dx = (originalWidth - zoomedWidth) / 2;
      const dy = (originalHeight - zoomedHeight) / 2;

      // 绘制放大后的图片，超出部分会被裁剪
      ctx.drawImage(
        img,
        0,
        0,
        originalWidth,
        originalHeight, // 源图片裁剪区域
        dx,
        dy,
        zoomedWidth,
        zoomedHeight // 目标绘制区域
      );
      let miniType = /\.jpg/.test(imageUrl) ? 'image/jpeg' : 'image/png';

      // 将Canvas转为DataURL
      const dataURL = canvas.toDataURL(miniType, 0.9);
      resolve(dataURL);
    };

    img.onerror = function () {
      reject(new Error('图片加载失败'));
      mwx.log.error('cropAndZoomImage:图片加载失败,imageUrl=' + imageUrl);
      mwx._getImageErrorCodeInfo(imageUrl);
    };

    img.src = imageUrl;
  });
}

/**
 * 处理图片上传，根据图片类型决定是否需要上传到永久存储
 *
 * @param {Object} params - 参数对象
 * @param {string} params.imageSrc - 图片 URL
 * @param {string} [params.fileName] - 文件名，用于临时图片处理（可选）
 * @returns {Promise<string>} 返回处理后的图片 URL
 *
 * @description
 * 处理流程：
 * 1. 判断图片类型（在线图片、临时图片）
 * 2. 临时图片且有 fileName：调用 v5MediaCopy 复制为永久存储
 * 3. 非在线图片或临时图片：调用 checkImage 上传到永久存储
 * 4. 在线图片且非临时图片：直接返回原始 URL
 */
async function processImageUpload({ imageSrc, fileName }) {
  // 判断图片类型，用于不同的处理方式
  let isOnlineImage = /^http/.test(imageSrc);
  let isTemporaryImage = imageSrc.indexOf('Expires') != -1;

  // 处理临时图片（包含过期时间的图片）
  if (isTemporaryImage && fileName) {
    try {
      let res = await mwx.api.v5MediaCopy({
        fileName: fileName
      });
      if (res && res.url) {
        return res.url;
      } else {
        throw new Error(mwx.failReasonMap.uploadFail);
      }
    } catch (error) {
      mwx.log.error('processImageUpload v5MediaCopy fail:', error);
      throw error;
    }
  }

  // 对于非在线图片或临时图片，需要上传到永久存储
  if (!isOnlineImage || isTemporaryImage) {
    try {
      const res = await mwx.api.checkImage(imageSrc, {
        permanent: 1,
        riskyCheck: 0,
        addData: {
          mediaType: 'png'
        }
      });

      if (res && res.url) {
        return res.url;
      } else {
        throw new Error(mwx.failReasonMap.uploadFail);
      }
    } catch (error) {
      mwx.log.error('processImageUpload checkImage fail:', error);
      throw error;
    }
  } else {
    // 如果是在线图片且不是临时图片，直接返回
    return imageSrc;
  }
}

/**
 * 上传 imageInfo.layers 中的临时图片
 * 遍历 layers 数组，检查每个 layer 的 imageUrl，如果是临时图片则上传到永久存储
 * @param {Object} imageInfo - 图片信息对象，包含 layers 数组
 * @returns {Promise<Object>} 返回更新后的 imageInfo 对象
 */
async function uploadImageInfoLayers(imageInfo) {
  if (!imageInfo) {
    return imageInfo;
  }

  // 收集所有需要上传的图片任务
  const uploadTasks = [];
  const enqueueLayerUploadTask = function (layer, done) {
    if (!layer || !layer.imageUrl) {
      return;
    }

    const imageUrl = layer.imageUrl;
    // 判断是否为临时图片：包含 'Expires' 或匹配 blob:/data: 开头
    const isLocalImageSrc = /^(data:|blob:)/.test(imageUrl);
    const isTemporaryImage = imageUrl && imageUrl.indexOf('Expires') != -1;

    // 获取 fileName（如果有）
    const fileName = urlFilenameMap && urlFilenameMap[imageUrl];
    if (isTemporaryImage || isLocalImageSrc) {
      uploadTasks.push(
        processImageUpload({
          imageSrc: imageUrl,
          fileName: fileName
        })
          .then((uploadedUrl) => {
            done(uploadedUrl);
            return { uploadedUrl };
          })
          .catch((error) => {
            mwx.log.error('uploadImageInfoLayers fail:', error);
            // 上传失败时保持原 URL，不抛出错误，避免影响其他图片上传
            done(imageUrl);
            return { uploadedUrl: imageUrl, error: error };
          })
      );
    }
  };

  if (imageInfo.layers && Array.isArray(imageInfo.layers)) {
    imageInfo.layers.forEach((layer, index) => {
      enqueueLayerUploadTask(layer, (uploadedUrl) => {
        if (imageInfo.layers[index]) {
          imageInfo.layers[index].imageUrl = uploadedUrl;
        }
      });
    });
  }

  if (imageInfo.reverseLayer && Array.isArray(imageInfo.reverseLayer)) {
    imageInfo.reverseLayer.forEach((layer, index) => {
      enqueueLayerUploadTask(layer, (uploadedUrl) => {
        if (imageInfo.reverseLayer[index]) {
          imageInfo.reverseLayer[index].imageUrl = uploadedUrl;
        }
      });
    });
  }

  if (imageInfo.background && imageInfo.background.imageUrl) {
    enqueueLayerUploadTask(imageInfo.background, (uploadedUrl) => {
      imageInfo.background.imageUrl = uploadedUrl;
    });
  }

  if (imageInfo.reverseBackground && imageInfo.reverseBackground.imageUrl) {
    enqueueLayerUploadTask(imageInfo.reverseBackground, (uploadedUrl) => {
      imageInfo.reverseBackground.imageUrl = uploadedUrl;
    });
  }

  // 等待所有上传任务完成
  if (uploadTasks.length > 0) {
    await Promise.all(uploadTasks);
  }

  return imageInfo;
}

async function parseWhiteInk({
  blobUrl,
  fileName,
  skipCheckIfRetouchImage,
  whiteShrink,
  whiteAction
}) {
  return blobUrl;
  // let url;
  // if (fileName) {
  //   let r = await mwx.api.v5ImageWhite({
  //     fileName,
  //     whiteShrink,
  //     whiteAction
  //   });
  //   window._debugLog &&
  //     window._debugLog({
  //       title: '后台解析白墨',
  //       白墨图: r.url
  //     });
  //   // let result = await mwx.canvasUtil.convertBackendWhiteInkToFrontendWhiteInk(
  //   //   r.url
  //   // );
  //   url = r.url;
  //   window._debugLog &&
  //     window._debugLog({
  //       title: '后台解析白墨',
  //       白墨图: url
  //     });
  //   return url;
  // }
  // let isTransparentImage =
  //   skipCheckIfRetouchImage || (await checkIfRetouchImage(blobUrl));
  // if (isTransparentImage) {
  //   let res = await mwx.api.checkImage(blobUrl, {
  //     permanent: 0,
  //     riskyCheck: 0
  //   });
  //   let r = await mwx.api.v5ImageWhite({
  //     fileName: res.fileName,
  //     whiteShrink,
  //     whiteAction
  //   });
  //   url = r.url;
  //   window._debugLog &&
  //     window._debugLog({
  //       title: '后台解析白墨',
  //       白墨图: url
  //     });
  // } else {
  //   url = await mwx.canvasUtil.convertWhiteToTransparent(blobUrl);
  // }
  // window._debugLog &&
  //   window._debugLog({
  //     title: '后台解析白墨',
  //     后台白墨图: url
  //   });
  // return url;
}

/**
 * 创建带缓存的方法包装器
 * 对方法的执行结果进行缓存，相同参数再次调用时直接返回缓存结果
 *
 * @param {Function} fn - 需要缓存结果的方法
 * @param {Object} [options] - 配置项
 * @param {number} [options.maxSize=20] - 缓存最大条目数
 * @param {Function} [options.keyGenerator] - 缓存 key 生成函数，参数为 fn 的 arguments
 * @param {number} [options.expirationTime] - 缓存过期时间（毫秒）
 * @returns {Function} 包装后的方法
 */
function createCachedMethod(fn, options) {
  const opts = options || {};
  const maxSize = opts.maxSize || 20;
  const keyGenerator = opts.keyGenerator;
  const expirationTime = opts.expirationTime;
  const cache = new Cache(maxSize);

  return async function (...args) {
    const cacheKey = keyGenerator
      ? keyGenerator.apply(null, args)
      : JSON.stringify(args);
    const cached = cache.get(cacheKey);
    if (cached !== undefined && cached !== null) {
      return Promise.resolve(cached);
    }

    const result = fn.apply(this, args);

    if (result && typeof result.then === 'function') {
      try {
        const res = await result;
        cache.set(cacheKey, res, expirationTime);
        return res;
      } catch (err) {
        throw err;
      }
    }

    cache.set(cacheKey, result, expirationTime);
    return Promise.resolve(result);
  };
}

/**
 * 计算自定义尺寸价格
 * @param {number} widthMm - 宽度(mm)
 * @param {number} heightMm - 高度(mm)
 * @returns {number} 价格(元)，保留2位小数
 */
function calcCustomSizePrice(widthMm, heightMm) {
  var customSizeData = (mwx.getWxData() || {}).customSizeData;
  if (!customSizeData) return 0;
  var tierPriceList = customSizeData.tierPriceList;
  var areaDiscountList = customSizeData.areaDiscountList;
  if (
    !tierPriceList ||
    !tierPriceList.length ||
    !areaDiscountList ||
    !areaDiscountList.length
  )
    return 0;

  var areaCm2 = (widthMm * heightMm) / 100;
  var discount = 1;
  for (var i = 0; i < areaDiscountList.length; i++) {
    var item = areaDiscountList[i];
    if (areaCm2 >= item.start && areaCm2 < item.end) {
      discount = item.discount;
      break;
    }
  }

  var price =
    customSizeData.additionalPrice + tierPriceList[0] * areaCm2 * discount;
  return Math.round(price * 100) / 100;
}

export {
  rotateAndMinifyImage,
  getCurrentList,
  divideListByCount,
  fixNoCropInfoData,
  getPldSizeInfo,
  checkImageIsSameColor,
  getFileMimeType,
  createAutoModeTemplate,
  createHorizontalAutoModeTemplate,
  handleTemplateImageList,
  getPbRotateImageSize,
  transformFileToImage,
  createChuXue,
  createRoundChuXue,
  getFontWidth,
  getAddTextFontInfo,
  promiseTimeout,
  handerGoodsDetail,
  filterString,
  getLifePhotoTextHeight,
  createClipImage,
  base64ToBlobUrl,
  checkImageTransparentPercent,
  checkIfRetouchImage,
  getOddCountBgColor,
  getTemplateBgColor,
  imageUrlToBase64,
  getToolList,
  triggerFileUpload,
  processImageUpload,
  uploadImageInfoLayers,
  parseWhiteInk,
  createCachedMethod,
  calcCustomSizePrice
};
